/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import {useLocation} from 'react-router-dom'

/**
 * Determines if the robots meta tag should be "index,follow"
 * Requirements:
 * - Page URL has no query parameters
 * - Page has a self-referencing canonical tag
 *
 * @param {string} canonicalUrl - The canonical URL for the page
 * @param {string} search - The current URL search/query string
 * @returns {boolean} - Whether to add index,follow robots tag
 */
const shouldIndexFollow = (canonicalUrl, search) => {
    // Must have a canonical URL
    if (!canonicalUrl) return false

    // Must have no query parameters
    const hasQueryParams = search && search.length > 1 // > 1 to account for just "?"
    if (hasQueryParams) return false

    return true
}

/**
 * Renders OpenGraph meta tags from an array of tag objects.
 *
 * @param {Array} openGraphTags - Array of objects with { property, content } for OG tags
 * @returns {Array} Array of meta elements for OpenGraph tags
 */
const renderOpenGraphTags = (openGraphTags) => {
    if (!Array.isArray(openGraphTags) || openGraphTags.length === 0) {
        return null
    }

    return openGraphTags
        .filter((tag) => tag?.property && tag?.content)
        .map((tag, index) => (
            <meta key={`og-${tag.property}-${index}`} property={tag.property} content={tag.content} />
        ))
}

const Seo = ({
    title,
    description,
    noIndex,
    noFollow,
    keywords,
    canonicalUrl,
    openGraphTags,
    children,
    ...props
}) => {
    const location = useLocation()

    // Determine robots meta tag value
    // Priority: noIndex/noFollow > indexFollow > no robots tag
    const indexFollow = shouldIndexFollow(canonicalUrl, location.search)

    // Build robots content based on noIndex and noFollow props
    const getRobotsContent = () => {
        if (noIndex && noFollow) return 'noindex, nofollow'
        if (noIndex) return 'noindex'
        if (noFollow) return 'nofollow'
        if (indexFollow) return 'index,follow'
        return null
    }
    const robotsContent = getRobotsContent()

    return (
        <Helmet {...props}>
            {title && <title>{title}</title>}
            {description && <meta name="description" content={description} />}
            {robotsContent && <meta name="robots" content={robotsContent} />}
            {keywords && <meta name="keywords" content={keywords} />}
            {renderOpenGraphTags(openGraphTags)}
            {children}
        </Helmet>
    )
}

Seo.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    noIndex: PropTypes.bool,
    noFollow: PropTypes.bool,
    children: PropTypes.node,
    keywords: PropTypes.string,
    canonicalUrl: PropTypes.string,
    openGraphTags: PropTypes.arrayOf(
        PropTypes.shape({
            property: PropTypes.string,
            content: PropTypes.string
        })
    )
}

export default Seo
